import React from 'react'
import { TableSkeleton, type TableSkeletonColumn } from '@components/ui'
import { SHOW_GROUP_PROCEDURES_BY_TOOTH_SETTING } from '@constants/Settings/chartingPreferences.constants'
import './SettingsSkeleton.css'

export type SettingsSkeletonVariant =
  | 'form'
  | 'table'
  | 'cards'
  | 'tiles'
  | 'procedure-cards'
  | 'goals'

type SettingsSkeletonProps = {
  variant?: SettingsSkeletonVariant
  pageKey?: string
  showToolbar?: boolean
  showTabs?: boolean
  showCodes?: boolean
}

const ROOM_OPERATORIES_COLUMNS: TableSkeletonColumn[] = [
  { width: '220px', kind: 'text' },
  { width: '220px', kind: 'badge' },
  { width: '100%', kind: 'badge' },
  { width: '96px', kind: 'actions' },
]

const STAFF_LIST_COLUMNS: TableSkeletonColumn[] = [
  { width: '100%', kind: 'text' },
  { width: '100%', kind: 'text' },
  { width: '120px', kind: 'badge' },
  { width: '100%', kind: 'text' },
  { width: '80px', kind: 'text' },
  { width: '148px', kind: 'text' },
  { width: '136px', kind: 'badge' },
  { width: '184px', kind: 'text' },
  { width: '160px', kind: 'actions' },
]

const WORK_SCHEDULE_COLUMNS: TableSkeletonColumn[] = [
  { width: '160px', kind: 'text' },
  ...Array.from(
    { length: 7 },
    (): TableSkeletonColumn => ({
      width: '100%',
      kind: 'text',
    }),
  ),
]

const PERMISSIONS_COLUMNS: TableSkeletonColumn[] = [
  { width: '20%', kind: 'text' },
  ...Array.from(
    { length: 7 },
    (): TableSkeletonColumn => ({
      width: '11%',
      kind: 'checkbox',
    }),
  ),
]

const PROCEDURE_CODES_COLUMNS: TableSkeletonColumn[] = [
  { width: '96px', kind: 'toggle' },
  { width: '156px', kind: 'text' },
  { width: '200px', kind: 'badge' },
  { width: '120px', kind: 'text' },
  { width: '110px', kind: 'text' },
  { width: '100%', kind: 'text' },
  { width: '96px', kind: 'actions' },
]

const DICOM_DEVICE_COLUMNS: TableSkeletonColumn[] = [
  { width: '160px', kind: 'badge' },
  { width: '100%', kind: 'text' },
  { width: '130px', kind: 'text' },
  { width: '100%', kind: 'text' },
  { width: '150px', kind: 'text' },
  { width: '210px', kind: 'text' },
  { width: '96px', kind: 'toggle' },
  { width: '240px', kind: 'actions' },
]

const DICOM_WATCH_FOLDER_COLUMNS: TableSkeletonColumn[] = [
  { width: '100%', kind: 'text' },
  { width: '100%', kind: 'text' },
  { width: '150px', kind: 'text' },
  { width: '110px', kind: 'text' },
  { width: '210px', kind: 'text' },
  { width: '96px', kind: 'toggle' },
  { width: '96px', kind: 'actions' },
]

const CARRIER_COLUMNS: TableSkeletonColumn[] = [
  { width: '100%', kind: 'text' },
  { width: '128px', kind: 'text' },
  { width: '136px', kind: 'text' },
  { width: '136px', kind: 'text' },
  { width: '96px', kind: 'text' },
  { width: '168px', kind: 'text' },
  { width: '160px', kind: 'actions' },
]

const FEE_SCHEDULE_COLUMNS: TableSkeletonColumn[] = [
  { width: '100%', kind: 'text' },
  { width: '100%', kind: 'text' },
  { width: '100%', kind: 'text' },
  { width: '100%', kind: 'text' },
  { width: '160px', kind: 'actions' },
]

const DISCOUNT_COLUMNS: TableSkeletonColumn[] = [
  { width: '88px', kind: 'toggle' },
  { width: '100%', kind: 'text' },
  { width: '240px', kind: 'text' },
  { width: '160px', kind: 'actions' },
]

const ADJUSTMENT_COLUMNS: TableSkeletonColumn[] = [
  { width: '88px', kind: 'toggle' },
  { width: '100%', kind: 'text' },
  { width: '100%', kind: 'text' },
  { width: '100%', kind: 'text' },
  { width: '160px', kind: 'actions' },
]

const MEMBERSHIP_COLUMNS: TableSkeletonColumn[] = [
  { width: '80px', kind: 'toggle' },
  { width: '160px', kind: 'text' },
  { width: '144px', kind: 'text' },
  { width: '144px', kind: 'text' },
  { width: '100%', kind: 'badge' },
  { width: '160px', kind: 'actions' },
]

const TABLE_COLUMNS_BY_PAGE: Record<string, TableSkeletonColumn[]> = {
  'accounting-adjustments': ADJUSTMENT_COLUMNS,
  'accounting-carrier': CARRIER_COLUMNS,
  'accounting-membership': MEMBERSHIP_COLUMNS,
  'accounting-discounts': DISCOUNT_COLUMNS,
  'accounting-fee-schedule': FEE_SCHEDULE_COLUMNS,
  'clinical-dicom-capture': DICOM_DEVICE_COLUMNS,
  'clinical-dicom-capture-watch-folders': DICOM_WATCH_FOLDER_COLUMNS,
  'practice-room-operatories': ROOM_OPERATORIES_COLUMNS,
  'staff-list': STAFF_LIST_COLUMNS,
  'staff-work-sched': WORK_SCHEDULE_COLUMNS,
  'staff-permissions': PERMISSIONS_COLUMNS,
  'clinical-procedure': PROCEDURE_CODES_COLUMNS,
  'system-cloud-storage': [
    { width: '100%', kind: 'text' },
    { width: '100%', kind: 'badge' },
    { width: '100%', kind: 'text' },
    { width: '100%', kind: 'text' },
  ],
}

const TABLE_LEAD_BLOCK_HEIGHTS: Record<string, number> = {
  'system-cloud-storage': 180,
}

const TABLE_FILTER_SKELETON_IDS = new Set([
  'comm-phone-management',
  'comm-call-flow',
  'comm-call-block',
  'comm-devices',
  'comm-call-record',
  'comm-fax',
  'system-cloud-storage',
])

const GOALS_COLUMNS: TableSkeletonColumn[] = [
  { width: '400px', kind: 'text' },
  { width: '100%', kind: 'text' },
]

const GOALS_COLLAPSED_SECTIONS = 6

const GoalsSkeleton: React.FC<{ showTabs: boolean }> = ({ showTabs }) => (
  <div
    className="settings-subpage-skeleton settings-subpage-skeleton--goals"
    aria-hidden="true"
  >
    {showTabs ? (
      <div className="settings-subpage-skeleton-goals-tabs">
        {Array.from({ length: 4 }).map((_, index) => (
          <span
            key={`goals-tab-${index}`}
            className="settings-subpage-skeleton-goals-tab settings-subpage-skeleton-shimmer"
          />
        ))}
      </div>
    ) : null}
    <div className="settings-subpage-skeleton-goals-section settings-subpage-skeleton-goals-section--open">
      <div className="settings-subpage-skeleton-goals-header">
        <span className="settings-subpage-skeleton-section-title" />
        <span className="settings-subpage-skeleton-goals-chevron" />
      </div>
      <TableSkeleton
        columns={GOALS_COLUMNS}
        rows={8}
        showToolbar={false}
        showPagination={false}
        fillHeight={false}
      />
    </div>
    {Array.from({ length: GOALS_COLLAPSED_SECTIONS }).map((_, index) => (
      <div
        key={`goals-section-${index}`}
        className="settings-subpage-skeleton-goals-section"
      >
        <div className="settings-subpage-skeleton-goals-header">
          <span className="settings-subpage-skeleton-section-title" />
          <span className="settings-subpage-skeleton-goals-chevron" />
        </div>
      </div>
    ))}
  </div>
)

const FilterSkeleton: React.FC = () => (
  <div className="settings-subpage-skeleton-filter-row">
    <div className="settings-subpage-skeleton-filter">
      <span className="mgc_search_2_line settings-subpage-skeleton-filter-search-icon" />
      <span className="settings-subpage-skeleton-filter-input settings-subpage-skeleton-shimmer" />
      <span className="mgc_settings_2_line settings-subpage-skeleton-filter-btn" />
    </div>
  </div>
)

type FormSectionRow =
  | string
  | { template: string; fields: number; kind?: 'field' | 'toggle' }

type FormSectionLayout = {
  maxWidth?: number
  teethPreview?: boolean
  blockPreviewHeight?: number
  tableColumns?: TableSkeletonColumn[]
  sections: { logo?: boolean; rows: FormSectionRow[] }[]
}

const QUARTERS = 'repeat(4, minmax(0, 1fr))'
const THIRDS = 'repeat(3, minmax(0, 1fr))'
const HALVES = 'repeat(2, minmax(0, 1fr))'
const SINGLE = 'minmax(0, 1fr)'
const CITY_STATE_ZIP = 'minmax(0, 1fr) minmax(0, 1fr) 160px'

const FORM_SECTION_LAYOUTS: Record<string, FormSectionLayout> = {
  'practice-clinic-info': {
    maxWidth: 640,
    sections: [
      { logo: true, rows: [THIRDS] },
      { rows: [THIRDS, SINGLE] },
      { rows: [SINGLE, SINGLE, CITY_STATE_ZIP, SINGLE] },
    ],
  },
  'practice-tax-and-legal': {
    maxWidth: 720,
    sections: [
      { rows: [THIRDS, THIRDS, HALVES] },
      { rows: [HALVES] },
      {
        rows: ['minmax(0, 1fr) 160px', SINGLE, SINGLE, CITY_STATE_ZIP],
      },
    ],
  },
  'clinical-charting-preference': {
    teethPreview: true,
    sections: [
      { rows: [QUARTERS, QUARTERS] },
      {
        rows: [
          {
            template: QUARTERS,
            fields: SHOW_GROUP_PROCEDURES_BY_TOOTH_SETTING ? 4 : 3,
          },
        ],
      },
    ],
  },
  'practice-scheduler': {
    blockPreviewHeight: 240,
    sections: [
      {
        rows: [
          HALVES,
          THIRDS,
          THIRDS,
          { template: THIRDS, fields: 9, kind: 'toggle' },
        ],
      },
      { rows: [THIRDS, THIRDS] },
    ],
  },
  'system-backup': {
    sections: [
      { rows: [THIRDS, { template: THIRDS, fields: 3, kind: 'toggle' }] },
    ],
    tableColumns: [
      { width: '100%', kind: 'text' },
      { width: '100%', kind: 'text' },
      { width: '100%', kind: 'badge' },
    ],
  },
}

const countGridColumns = (template: string): number =>
  template.startsWith('repeat(')
    ? Number(template.slice(7, template.indexOf(',')))
    : template.replace(/minmax\([^)]*\)/g, 'm').split(/\s+/).length

const FieldSkeleton: React.FC = () => (
  <div className="settings-subpage-skeleton-field">
    <span className="settings-subpage-skeleton-line" />
    <span className="settings-subpage-skeleton-input settings-subpage-skeleton-shimmer" />
  </div>
)

const ToggleFieldSkeleton: React.FC = () => (
  <div className="settings-subpage-skeleton-toggle-field">
    <span className="settings-subpage-skeleton-oh-toggle" />
    <span className="settings-subpage-skeleton-line" />
  </div>
)

const SectionFormSkeleton: React.FC<{ layout: FormSectionLayout }> = ({
  layout,
}) => (
  <div
    className="settings-subpage-skeleton settings-subpage-skeleton--sections"
    style={{ maxWidth: layout.maxWidth }}
    aria-hidden="true"
  >
    {layout.teethPreview ? (
      <div className="settings-subpage-skeleton-teeth">
        {Array.from({ length: 16 }).map((_, index) => (
          <div
            key={`tooth-${index}`}
            className="settings-subpage-skeleton-tooth"
          >
            <span className="settings-subpage-skeleton-tooth-number" />
            <span className="settings-subpage-skeleton-tooth-buccal settings-subpage-skeleton-shimmer" />
            <span className="settings-subpage-skeleton-tooth-crown settings-subpage-skeleton-shimmer" />
          </div>
        ))}
      </div>
    ) : null}
    {layout.sections.map((section, sectionIndex) => (
      <div
        key={`section-${sectionIndex}`}
        className="settings-subpage-skeleton-section"
      >
        <span className="settings-subpage-skeleton-section-title" />
        {sectionIndex === 0 && layout.blockPreviewHeight ? (
          <span
            className="settings-subpage-skeleton-block-preview settings-subpage-skeleton-shimmer"
            style={{ height: layout.blockPreviewHeight }}
          />
        ) : null}
        {section.logo ? (
          <div className="settings-subpage-skeleton-logo-field">
            <span className="settings-subpage-skeleton-line" />
            <div className="settings-subpage-skeleton-logo-block">
              <span className="settings-subpage-skeleton-logo-preview settings-subpage-skeleton-shimmer" />
              <div className="settings-subpage-skeleton-logo-actions">
                <span className="settings-subpage-skeleton-button" />
                <span className="settings-subpage-skeleton-button" />
                <span className="settings-subpage-skeleton-hint" />
              </div>
            </div>
          </div>
        ) : null}
        {section.rows.map((row, rowIndex) => {
          const template = typeof row === 'string' ? row : row.template
          const fields =
            typeof row === 'string' ? countGridColumns(row) : row.fields
          const isToggleRow = typeof row !== 'string' && row.kind === 'toggle'
          return (
            <div
              key={`row-${rowIndex}`}
              className={`settings-subpage-skeleton-section-row${
                isToggleRow
                  ? ' settings-subpage-skeleton-section-row--toggles'
                  : ''
              }`}
              style={
                {
                  '--settings-skeleton-columns': template,
                } as React.CSSProperties
              }
            >
              {Array.from({ length: fields }).map((_, fieldIndex) =>
                isToggleRow ? (
                  <ToggleFieldSkeleton key={`toggle-${fieldIndex}`} />
                ) : (
                  <FieldSkeleton key={`field-${fieldIndex}`} />
                ),
              )}
            </div>
          )
        })}
      </div>
    ))}
    {layout.tableColumns ? (
      <div className="settings-subpage-skeleton-section">
        <span className="settings-subpage-skeleton-section-title" />
        <TableSkeleton
          columns={layout.tableColumns}
          rows={8}
          showToolbar={false}
          showPagination={false}
          fillHeight={false}
        />
      </div>
    ) : null}
  </div>
)

const OPENING_HOURS_TABLE_COLUMNS: TableSkeletonColumn[] = [
  { width: '100%', kind: 'text' },
  { width: '100%', kind: 'text' },
  { width: '100%', kind: 'text' },
  { width: '100%', kind: 'badge' },
  { width: '96px', kind: 'actions' },
]

const OpeningHoursSkeleton: React.FC = () => (
  <div
    className="settings-subpage-skeleton settings-subpage-skeleton--opening-hours"
    aria-hidden="true"
  >
    <div className="settings-subpage-skeleton-oh-section">
      <span className="settings-subpage-skeleton-section-title" />
      <div className="settings-subpage-skeleton-oh-first-day">
        <span className="settings-subpage-skeleton-line" />
        <span className="settings-subpage-skeleton-input settings-subpage-skeleton-shimmer" />
      </div>
      <span className="settings-subpage-skeleton-oh-checkbox" />
      <div className="settings-subpage-skeleton-oh-days">
        {Array.from({ length: 7 }).map((_, index) => (
          <div
            key={`day-${index}`}
            className="settings-subpage-skeleton-oh-day"
          >
            <div className="settings-subpage-skeleton-oh-day-toggle">
              <span className="settings-subpage-skeleton-oh-toggle" />
              <span className="settings-subpage-skeleton-oh-day-name" />
            </div>
            <span className="settings-subpage-skeleton-input settings-subpage-skeleton-oh-time settings-subpage-skeleton-shimmer" />
            <span className="settings-subpage-skeleton-input settings-subpage-skeleton-oh-time settings-subpage-skeleton-shimmer" />
          </div>
        ))}
      </div>
    </div>
    {['override', 'holiday'].map((key) => (
      <div key={key} className="settings-subpage-skeleton-oh-section">
        <div className="settings-subpage-skeleton-oh-header">
          <span className="settings-subpage-skeleton-section-title" />
          <span className="settings-subpage-skeleton-button settings-subpage-skeleton-button--md" />
        </div>
        <TableSkeleton
          columns={OPENING_HOURS_TABLE_COLUMNS}
          rows={3}
          showToolbar={false}
          showPagination={false}
          fillHeight={false}
        />
      </div>
    ))}
  </div>
)

const APPEARANCE_OPTION_COUNT = 3

const AppearanceSkeleton: React.FC = () => (
  <div
    className="settings-subpage-skeleton settings-subpage-skeleton--appearance"
    aria-hidden="true"
  >
    <div className="settings-subpage-skeleton-appearance-card">
      <span className="settings-subpage-skeleton-section-title" />
      <span className="settings-subpage-skeleton-line settings-subpage-skeleton-appearance-description" />
      <div className="settings-subpage-skeleton-appearance-options">
        {Array.from({ length: APPEARANCE_OPTION_COUNT }).map((_, index) => (
          <div
            key={`appearance-option-${index}`}
            className="settings-subpage-skeleton-appearance-option settings-subpage-skeleton-shimmer"
          >
            <span className="settings-subpage-skeleton-line settings-subpage-skeleton-appearance-option-title" />
            <span className="settings-subpage-skeleton-line" />
          </div>
        ))}
      </div>
    </div>
  </div>
)

const SettingsSkeleton: React.FC<SettingsSkeletonProps> = ({
  variant = 'form',
  pageKey,
  showTabs = true,
  showCodes = true,
}) => {
  if (variant === 'goals') {
    return <GoalsSkeleton showTabs={showTabs} />
  }

  if (variant === 'procedure-cards') {
    return (
      <div
        className="settings-subpage-skeleton settings-subpage-skeleton--procedure-cards"
        aria-hidden="true"
      >
        {showTabs ? (
          <div className="settings-subpage-skeleton-tabs">
            <span className="settings-subpage-skeleton-tab settings-subpage-skeleton-shimmer" />
            <span className="settings-subpage-skeleton-tab settings-subpage-skeleton-shimmer" />
          </div>
        ) : null}
        <div className="settings-subpage-skeleton-procedure-grid">
          {Array.from({ length: 9 }).map((_, index) => (
            <div
              key={`procedure-card-${index}`}
              className="settings-subpage-skeleton-procedure-card"
            >
              <div className="settings-subpage-skeleton-procedure-card-header">
                <span className="settings-subpage-skeleton-procedure-card-title settings-subpage-skeleton-shimmer" />
                <div className="settings-subpage-skeleton-procedure-card-actions">
                  <span className="settings-subpage-skeleton-procedure-card-icon" />
                  <span className="settings-subpage-skeleton-procedure-card-icon" />
                </div>
              </div>
              {showCodes ? (
                <div className="settings-subpage-skeleton-procedure-card-codes">
                  {Array.from({ length: 3 }).map((__, codeIndex) => (
                    <span
                      key={`procedure-code-${codeIndex}`}
                      className="settings-subpage-skeleton-procedure-card-code settings-subpage-skeleton-shimmer"
                    />
                  ))}
                </div>
              ) : null}
              <span className="settings-subpage-skeleton-procedure-card-date" />
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (variant === 'table') {
    const showFilter = TABLE_FILTER_SKELETON_IDS.has(pageKey ?? '')
    const leadBlockHeight = TABLE_LEAD_BLOCK_HEIGHTS[pageKey ?? '']

    return (
      <div
        className="settings-subpage-skeleton settings-subpage-skeleton--table"
        aria-hidden="true"
      >
        {leadBlockHeight ? (
          <span
            className="settings-subpage-skeleton-block-preview settings-subpage-skeleton-shimmer"
            style={{ height: leadBlockHeight }}
          />
        ) : null}
        {showFilter ? <FilterSkeleton /> : null}
        <div className="settings-subpage-skeleton-table-wrap">
          <TableSkeleton
            columns={TABLE_COLUMNS_BY_PAGE[pageKey ?? '']}
            rows={8}
            showToolbar={false}
            showPagination
            actionCount={2}
          />
        </div>
      </div>
    )
  }

  if (variant === 'cards') {
    return (
      <div
        className="settings-subpage-skeleton settings-subpage-skeleton--cards"
        aria-hidden="true"
      >
        {Array.from({ length: 2 }).map((_, index) => (
          <div
            key={`integration-card-${index}`}
            className="settings-subpage-skeleton-card"
          >
            <span className="settings-subpage-skeleton-card-icon settings-subpage-skeleton-shimmer" />
            <div className="settings-subpage-skeleton-card-copy">
              <span className="settings-subpage-skeleton-card-title" />
              <span className="settings-subpage-skeleton-card-status" />
            </div>
            <span className="settings-subpage-skeleton-chip settings-subpage-skeleton-shimmer" />
          </div>
        ))}
      </div>
    )
  }

  if (variant === 'tiles') {
    return (
      <div
        className="settings-subpage-skeleton settings-subpage-skeleton--tiles"
        aria-hidden="true"
      >
        <span className="settings-subpage-skeleton-title" />
        <div className="settings-subpage-skeleton-tile-grid">
          {Array.from({ length: 6 }).map((_, index) => (
            <div
              key={`persona-tile-${index}`}
              className="settings-subpage-skeleton-tile"
            >
              <span className="settings-subpage-skeleton-tile-icon settings-subpage-skeleton-shimmer" />
              <span className="settings-subpage-skeleton-tile-label settings-subpage-skeleton-shimmer" />
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (pageKey === 'practice-opening-hours') {
    return <OpeningHoursSkeleton />
  }

  if (pageKey === 'system-appearance') {
    return <AppearanceSkeleton />
  }

  const sectionLayout = pageKey ? FORM_SECTION_LAYOUTS[pageKey] : undefined
  if (sectionLayout) {
    return <SectionFormSkeleton layout={sectionLayout} />
  }

  return (
    <div
      className="settings-subpage-skeleton settings-subpage-skeleton--form"
      aria-hidden="true"
    >
      <span className="settings-subpage-skeleton-title" />
      {pageKey === 'practice-scheduler' ||
      pageKey === 'system-backup' ||
      pageKey === 'comm-ai-receptionist' ? null : (
        <div className="settings-subpage-skeleton-logo-row">
          <span className="settings-subpage-skeleton-logo settings-subpage-skeleton-shimmer" />
          <span className="settings-subpage-skeleton-chip" />
          <span className="settings-subpage-skeleton-chip" />
        </div>
      )}
      <div className="settings-subpage-skeleton-fields">
        {Array.from({ length: 8 }).map((_, index) => (
          <div
            key={`field-${index}`}
            className={`settings-subpage-skeleton-field${
              index === 7 ? ' settings-subpage-skeleton-field--wide' : ''
            }`}
          >
            <span className="settings-subpage-skeleton-line" />
            <span className="settings-subpage-skeleton-input settings-subpage-skeleton-shimmer" />
          </div>
        ))}
      </div>
    </div>
  )
}

export default SettingsSkeleton
